import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

const ProjectItem = ({ project }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const imgRef = useRef(null);
  const overlayRef = useRef(null);

  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  const handleScroll = useCallback(() => {
    if (isMobile && imgRef.current && overlayRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const visiblePercentage = Math.min(Math.max((rect.bottom - windowHeight) / rect.height, 0), 1);
      const scale = 1 + (0.5 - visiblePercentage) * 0.1; // Adjust the scale factor as needed
      imgRef.current.style.transform = `scale(${scale})`;
      overlayRef.current.style.transform = `scale(${scale})`;
    }
  }, [isMobile]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleResize, handleScroll]);

  const containerStyle = {
    overflow: 'hidden', // Hide any overflow content
    position: 'relative', // Add position relative to position the title and overlay
    borderRadius: '20px', // Curved corners
    height: '100%', // Ensure the container takes the full height
  };

  const imageStyle = {
    width: '100%', // Make the image fill the container horizontally
    height: '100%', // Make the image fill the container vertically
    transition: 'transform 0.2s', // Add a transition effect to the transform property
    transform: isHovered ? 'scale(1.1)' : 'scale(1)', // Scale up the image by 10% on hover
    borderRadius: '20px', // Curved corners for the image
    display: 'block', // Ensure the image is a block element
  };

  const overlayContainerStyle = {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '20px', // Curved corners for the overlay container
  };

  const overlayStyle = {
    background: 'url("https://framerusercontent.com/images/rR6HYXBrMmX4cRpXfXUOvpvpB0.png") repeat',
    opacity: 0.1,
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    transition: 'transform 0.2s', // Add a transition effect to the transform property
    animation: 'staticNoise 0.1s steps(10) infinite', // Add animation
    pointerEvents: 'none', // Prevent the overlay from interfering with mouse events
    borderRadius: '20px', // Curved corners for the overlay
  };

  const iconContainerStyle = {
    position: 'absolute',
    top: '10px',
    right: isHovered ? '10px' : '-50px', // Adjust position based on hover state
    transition: 'right 0.3s ease', // Smooth transition for the icon
    width: '40px',
    height: '40px',
    backgroundColor: 'white', // Background color for the circle
    borderRadius: '50%', // Make it a circle
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const categoryStyle = {
    position: 'absolute',
    top: '10px',
    left: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.4)', // Semi-transparent background
    color: 'white', // Text color
    padding: '5px 10px', // Padding inside the label
    borderRadius: '20px', // Curved corners
    fontSize: '16px', // Font size
    zIndex: 1, // Ensure it stays above the image
  };

  useEffect(() => {
    // Add CSS for keyframes with random positions
    const createStaticNoiseKeyframes = () => {
      return `
        @keyframes staticNoise {
          0% { background-position: 0% 0%; }
          100% { background-position: 100% 100%; }
        }
      `;
    };

    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = createStaticNoiseKeyframes();
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <div
      style={containerStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/work/${project.id}`}>
        <div style={overlayContainerStyle}>
          <img
            ref={imgRef}
            src={project.image}
            alt={project.title}
            style={imageStyle}
          />
          <div ref={overlayRef} style={overlayStyle}></div> {/* Overlay image */}
          <div style={categoryStyle}>{project.category}</div> {/* Category label */}
          <div style={iconContainerStyle}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 256 256"
              focusable="false"
              style={{
                userSelect: 'none',
                width: '20px',
                height: '20px',
                display: 'inline-block',
                fill: 'black', // Inside color of the arrow
                flexShrink: 0,
              }}
            >
              <g weight="regular">
                <path d="M200,64V168a8,8,0,0,1-16,0V83.31L69.66,197.66a8,8,0,0,1-11.32-11.32L172.69,72H88a8,8,0,0,1,0-16H192A8,8,0,0,1,200,64Z"></path>
              </g>
            </svg>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ProjectItem;
